/**
 * SSO Validator
 * Valida todas las condiciones necesarias para SSO
 * Principio: Single Responsibility - Solo validación
 */

import { hasValidReferer, getRefererHostname, getRefererBaseDomain } from '../../utils/refererUtils';
import { SSOConfig, detectSSProvider } from './SSOConfig';

/**
 * Resultado de validación
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Si la validación pasó
 * @property {string} status - Estado de la validación
 * @property {string} [message] - Mensaje descriptivo
 * @property {*} [data] - Datos adicionales
 */

export class SSOValidator {
  /**
   * Valida si se puede intentar SSO (verifica referer y flags)
   * @returns {ValidationResult}
   */
  static canAttemptSSO() {
    // 1. Verificar si ya se intentó SSO recientemente
    const attempted = this.hasRecentAttempt();
    if (attempted.isValid === false) {
      return attempted;
    }

    // 2. Verificar si hay referer válido
    const refererCheck = this.validateReferer();
    if (!refererCheck.isValid) {
      return refererCheck;
    }

    // 3. Detectar el proveedor SSO basado en el hostname original
    const { hostname } = refererCheck.data;
    const provider = detectSSProvider(hostname); // Usar hostname original, no baseDomain

    return {
      isValid: true,
      status: SSOConfig.STATUS.PENDING,
      message: 'SSO can be attempted',
      data: {
        referer: refererCheck.data.referer,
        hostname: refererCheck.data.hostname,
        baseDomain: refererCheck.data.baseDomain,
        provider, // Proveedor detectado basado en hostname original
      },
    };
  }

  /**
   * Valida si hay un referer válido
   * @returns {ValidationResult}
   */
  static validateReferer() {
    if (!hasValidReferer()) {
      return {
        isValid: false,
        status: SSOConfig.STATUS.NO_REFERER,
        message: 'No valid referer found',
      };
    }

    const hostname = getRefererHostname();
    if (!hostname) {
      return {
        isValid: false,
        status: SSOConfig.STATUS.INVALID_DOMAIN,
        message: 'Cannot extract hostname from referer',
      };
    }

    // Extraer dominio base (sin primer subdominio)
    const baseDomain = getRefererBaseDomain();
    if (!baseDomain) {
      return {
        isValid: false,
        status: SSOConfig.STATUS.INVALID_DOMAIN,
        message: 'Cannot extract base domain from referer',
      };
    }

    return {
      isValid: true,
      status: SSOConfig.STATUS.PENDING,
      message: 'Valid referer found',
      data: {
        referer: document.referrer,
        hostname,
        baseDomain, // Dominio base sin primer subdominio (ej: campus.aulasneo.com)
      },
    };
  }

  /**
   * Valida la respuesta del backend SSO
   * @param {Response} response - Respuesta HTTP
   * @param {Object} data - Datos de la respuesta
   * @returns {ValidationResult}
   */
  static validateBackendResponse(response, data) {
    // Verificar status HTTP
    if (!response.ok) {
      if (response.status === 404) {
        return {
          isValid: false,
          status: SSOConfig.STATUS.INVALID_DOMAIN,
          message: 'Domain not configured for SSO (404)',
          data: { statusCode: 404 },
        };
      }

      return {
        isValid: false,
        status: SSOConfig.STATUS.BACKEND_ERROR,
        message: `Backend error: ${response.status}`,
        data: { statusCode: response.status },
      };
    }

    // Verificar estructura de datos
    if (!data || typeof data !== 'object') {
      return {
        isValid: false,
        status: SSOConfig.STATUS.BACKEND_ERROR,
        message: 'Invalid response data structure',
      };
    }

    // Verificar que tenga los datos necesarios para SSO
    if (!data.sso_enabled) {
      return {
        isValid: false,
        status: SSOConfig.STATUS.INVALID_DOMAIN,
        message: 'SSO not enabled for this domain',
        data,
      };
    }

    // Validar que tenga tokens si el SSO fue exitoso
    if (data.user && (!data.access_token || !data.refresh_token)) {
      return {
        isValid: false,
        status: SSOConfig.STATUS.BACKEND_ERROR,
        message: 'Missing authentication tokens',
        data,
      };
    }

    return {
      isValid: true,
      status: SSOConfig.STATUS.SUCCESS,
      message: 'Valid SSO response',
      data,
    };
  }

  /**
   * Verifica si hubo un intento reciente de SSO
   * @returns {ValidationResult}
   */
  static hasRecentAttempt() {
    try {
      const attempted = localStorage.getItem(SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED);
      if (!attempted) {
        return { isValid: true, message: 'No recent attempt' };
      }

      const attemptData = JSON.parse(attempted);
      const now = Date.now();
      const elapsed = now - attemptData.timestamp;

      // Si pasó el TTL, permitir nuevo intento
      if (elapsed > SSOConfig.ATTEMPT_TTL_MS) {
        localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED);
        return { isValid: true, message: 'Attempt expired' };
      }

      // Si el intento anterior fue exitoso, no reintentar
      if (attemptData.status === SSOConfig.STATUS.SUCCESS) {
        return {
          isValid: false,
          status: SSOConfig.STATUS.SUCCESS,
          message: 'SSO already successful',
        };
      }

      // Si hubo un fallo reciente, no reintentar
      if (attemptData.status === SSOConfig.STATUS.FAILED ||
          attemptData.status === SSOConfig.STATUS.BACKEND_ERROR ||
          attemptData.status === SSOConfig.STATUS.INVALID_DOMAIN) {
        return {
          isValid: false,
          status: attemptData.status,
          message: 'Recent SSO attempt failed, not retrying to avoid backend overload',
          data: attemptData,
        };
      }

      return { isValid: true, message: 'Can retry' };
    } catch (error) {
      console.warn('[SSO Validator] Error checking recent attempt:', error);
      return { isValid: true, message: 'Error checking, allowing attempt' };
    }
  }

  /**
   * Valida los datos de usuario recibidos del backend
   * @param {Object} userData - Datos del usuario
   * @returns {ValidationResult}
   */
  static validateUserData(userData) {
    if (!userData || typeof userData !== 'object') {
      return {
        isValid: false,
        message: 'Invalid user data',
      };
    }

    // Validar campos mínimos requeridos
    const requiredFields = ['username', 'email'];
    const missingFields = requiredFields.filter(field => !userData[field]);

    if (missingFields.length > 0) {
      return {
        isValid: false,
        message: `Missing required fields: ${missingFields.join(', ')}`,
      };
    }

    return {
      isValid: true,
      message: 'Valid user data',
    };
  }
}
